<template>
  <div class="service-page" ref="mainElement">
    <main class="main default-layout">
      <div class="headerreplacement" v-if="!page?.headerModule || !page.headerModule">
        <h1 class="_hidden" v-if="page?.title">{{ page.title }}</h1>
      </div>

      <component
        v-if="page?.headerModule"
        :key="page.headerModule.id"
        :is="HeaderModules[page.headerModule.__typename]"
        :data="page.headerModule"
      ></component>

      <component
        v-for="module in page?.contentModules"
        :key="module.id"
        :is="ContentModules[module.__typename]"
        :data="module"
      ></component>

      <ServiceContentServices
        :service-name="page?.title ?? ''"
        :projects="projects as ProjectFragment[]"
        :cta="page?.ctaModule as CtaModuleFragment"
      />
    </main>
    <SiteFooter theme="dark" />
  </div>
</template>

<script setup lang="ts">
import { ContentModules } from '~/components/content-modules/ContentModules';
import { HeaderModules } from '~/components/header-modules/HeaderModules';
import ServiceContentServices from '~/components/service-modules/ServiceContentServices.vue';
import { type SeoMetaTagType, toHead } from 'vue-datocms';
import { buildPageStructuredDataJsonLd } from '~/utils/structured-data';
import type {
  GetServiceBySlugQuery,
  CtaModuleFragment,
  ProjectFragment,
  GetProjectsByServiceQuery
} from '#gql';

const props = defineProps();

const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const { params } = route;

const { data, error } = await useAsyncGql({
  operation: 'GetServiceBySlug',
  variables: { slug: String(params.service_id) }
});

const page = (data.value as GetServiceBySlugQuery).service;

if (!page || error.value) {
  showError({
    statusCode: 404
  });
}

const { data: dataProjects, error: errorProjects } = await useAsyncGql({
  operation: 'GetProjectsByService',
  variables: { id: data.value.service?.id }
});

const projects = (dataProjects.value as GetProjectsByServiceQuery).allProjects;

const mainElement = ref<HTMLElement | null>(null);

useHead(toHead(page?.seo as SeoMetaTagType[]));

const pageStructuredData = computed(() => {
  const base = (runtimeConfig.public.baseUrl || '').replace(/\/$/, '');
  const path = route.path === '/' ? '/' : route.path.replace(/\/+$/, '');
  const serviceUrl = `${base}${path}`;

  return buildPageStructuredDataJsonLd({
    contentModules: page?.contentModules,
    service: page,
    serviceUrl
  });
});

useHead(() => ({
  script: pageStructuredData.value
    ? [
      {
        key: 'service-page-structured-data',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(pageStructuredData.value)
      }
    ]
    : []
}));
</script>

<style scoped lang="scss">
.service-page-enter-active,
.service-page-leave-active {
}

.service-page-leave-to {
}

.service-page {
  > .main {
    max-width: 100%;
    overflow-x: clip;
    margin-left: var(--site-outer-margin);
    margin-right: var(--site-outer-margin);

    > .inner {
      background-color: var(--color-off-white);
      z-index: 10;
      position: relative;

      > .divider {
        background-color: var(--color-off-white);

        @media (--vs) {
          height: 0;
        }
        @media (--vl) {
          height: 6rem;
        }
      }
    }
  }
}
</style>
